
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ShiftWarningModal from './ShiftWarningModal';
import { toast } from 'react-toastify';

const ShiftMonitor = () => {
    const { shift, endShift, continueOnOT } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [warningLevel, setWarningLevel] = useState(null); // 'warning' (15m) or 'critical' (5m)
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [debugEndTime, setDebugEndTime] = useState(null);

    // Initial check on mount
    useEffect(() => {
        if (shift) checkShiftTime();
    }, [shift]);

    // Periodic check
    useEffect(() => {
        if (!shift) return;

        const interval = setInterval(() => {
            checkShiftTime();
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, [shift, warningLevel]);

    const checkShiftTime = () => {
        if (!shift || !shift.startTime) return;

        const now = new Date();
        const start = new Date(shift.startTime);
        let end = new Date(start);

        if (shift.type === 'day') {
            // Day shift ends at 20:00 (8 PM) on the same day as start
            end.setHours(20, 0, 0, 0);

            // Handle edge case: if start was somehow after 20:00 (shouldn't happen for new day shift)
            // But if shift persisted from yesterday? server auto-closes shifts?
            // Assuming simplified logic: Day shift is always for the start date.
        } else {
            // Night shift always ends at 08:00 (8 AM)
            // Logic: The *next* 8 AM after start time.
            end.setHours(8, 0, 0, 0);
            if (end <= start) {
                end.setDate(end.getDate() + 1);
            }
        }

        const diffMs = end - now;
        const diffMinutes = Math.floor(diffMs / 60000);

        // Debugging logs if needed
        // console.log(`Shift monitor: ${shift.type}, End: ${end.toLocaleTimeString()}, Remaining: ${diffMinutes}m`);

        if (diffMinutes <= 15 && diffMinutes > 5) {
            // Warning level (15 min)
            if (warningLevel !== 'warning') {
                setWarningLevel('warning');
                setShowModal(true);
                // Optional: Check sessionStorage to avoid repeated modal if dismissed?
                // For now, simple logic.
            }
        } else if (diffMinutes <= 5 && diffMinutes > -60) {
            // Critical level (5 min) - Allow up to 1 hour past end just in case
            if (warningLevel !== 'critical') {
                setWarningLevel('critical');
                setShowModal(true);
                playAlertSound();
            }
        } else {
            // Reset if outside warning zone (e.g. shift extended or just started)
            if (warningLevel) {
                setWarningLevel(null);
                setShowModal(false);
            }
        }

        setTimeRemaining(diffMinutes);
        setDebugEndTime(end.toLocaleTimeString());
    };

    const playAlertSound = () => {
        // Simple browser beep logic or placeholder
        // Since we don't have an asset, we'll rely on visual
        // Or create an AudioContext beep
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (AudioContext) {
                const ctx = new AudioContext();
                const osc = ctx.createOscillator();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(440, ctx.currentTime);
                osc.connect(ctx.destination);
                osc.start();
                osc.stop(ctx.currentTime + 0.5); // Beep for 0.5s
            }
        } catch (e) {
            console.error('Audio play failed', e);
        }
    };

    const handleAction = async (action) => {
        if (action === 'ot') {
            const result = await continueOnOT();
            if (result.success) {
                const on = result.otMarkedOn
                    ? ` · OT marked on your attendance for ${new Date(`${result.otMarkedOn}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`
                    : ' · ask Admin to mark OT on your attendance';
                toast.success(`OT shift started${on}`);
                setShowModal(false);
                setWarningLevel(null);
            } else {
                toast.error(result.error);
            }
            return;
        }
        if (action === 'end') {
            const result = await endShift();
            if (result.success) {
                toast.success('Shift ended successfully');
                setShowModal(false);
                setWarningLevel(null);
            } else {
                toast.error(result.error || 'Failed to end shift');
            }
        } else {
            // Dismiss
            setShowModal(false);
            // Don't clear warningLevel so it doesn't pop up again immediately for the same level?
            // Actually, if we set warningLevel, next interval check will see it matches current level and skip setShowModal(true).
            // This prevents re-opening. Correct.
        }
    };

    return (
        <ShiftWarningModal
            show={showModal}
            onClose={() => handleAction('dismiss')}
            onAction={handleAction}
            timeRemaining={timeRemaining}
            canContinueOT={!!shift && !shift.isOt}
            nextShift={shift?.type === 'day' ? 'night' : 'day'}
        />
    );
};

export default ShiftMonitor;
